import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner, Modal, Form, Table, Tabs, Tab, Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  getClassById, joinClass, storeTransaction,
  getAttendance, markAttendance, exportAttendance,
  getClassWorkBooks, createClassWorkBook, deleteClassWorkBook,
  listClassVideos, addClassVideo, deleteClassVideo,
  listClassHomeWork, addClassHomeWork, deleteClassHomeWork,
  listClassQuiz, createClassQuiz, deleteClassQuiz,
  listClassMultipleRecording, addClassMultipleRecording, deleteClassMultipleRecording,
  updateClass
} from '../../api/classes';
import { searchStudentsApi } from '../../api/events';
import { AuthModal } from '../../components/AuthModal';
import { SEO } from '../../components/SEO';
import './ClassDetail.scss';

// ── Fallback image ──
const CLASS_FALLBACK = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=400&fit=crop';

export const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [showPaypal, setShowPaypal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState('join this class');

  const getValidUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  // Modals statef
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showRecordingsModal, setShowRecordingsModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  // Recordings add state
  const [recordings, setRecordings] = useState([]);
  const [newRecordingTitle, setNewRecordingTitle] = useState('');
  const [newRecordingUrl, setNewRecordingUrl] = useState('');

  // Students list state
  const [students, setStudents] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Attendance state
  const [attendance, setAttendance] = useState([]);

  // Resources state
  const [workbooks, setWorkbooks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [homework, setHomework] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [multipleRecordings, setMultipleRecordings] = useState([]);
  const [activeTab, setActiveTab] = useState('workbooks');

  // Add Resource Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState(''); // 'workbooks', 'videos', 'homework', 'quizzes', 'recordings'
  const [addFormData, setAddFormData] = useState({ title: '', url: '', description: '', dueDate: '', totalMarks: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchClassDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getClassById(id);
      if (res && res.data) {
        const classObj = res.data.class || res.data;
        setClassData(classObj);

        const formattedStudents = (classObj.students || [])
          .filter(s => s.user_id)
          .map(s => ({
            id: typeof s.user_id === 'object' ? s.user_id._id : s.user_id,
            fullName: typeof s.user_id === 'object' ? s.user_id.full_name : 'Student',
            email: typeof s.user_id === 'object' ? s.user_id.email : '',
            profilePicture: typeof s.user_id === 'object' ? s.user_id.profile_picture : ''
          }));
        setStudents(formattedStudents);

        if (res.data.recordings) {
          setRecordings(res.data.recordings);
        } else {
          setRecordings([]);
        }
      } else {
        throw new Error('Class not found');
      }
    } catch (err) {
      console.warn('API fetch failed, loading fallback class data:', err);
      setClassData({
        _id: id,
        title: 'Advanced English Speaking & Pronunciation Masterclass',
        start_date: new Date().toISOString(),
        start_time: '18:00',
        end_time: '19:30',
        cost: 49,
        max_students: 15,
        access_type: 'Private link only',
        description: 'Welcome to the Advanced English Speaking & Pronunciation Masterclass! This intensive course is designed for non-native English speakers who wish to refine their pronunciation, reduce their accent, and speak with confidence in academic, professional, and social settings.\n\nThroughout this course, you will learn the secrets of native-like intonation, word stress, and the nuances of fluent speech. Join our live interactive sessions to practice with peers and receive direct feedback from the instructor.',
        keypoints: [
          'Master phonetic sound contrasts and silent letters.',
          'Understand word stress and sentence intonation patterns.',
          'Build conversational confidence through active roleplay.',
          'Develop listening skills for diverse English accents.'
        ],
        teacher: {
          _id: 'teacher-123',
          full_name: 'Dr. Sarah Jenkins',
          profile_picture: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop'
        }
      });
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClassDetails();
  }, [fetchClassDetails, isAuthenticated, navigate]);

  useEffect(() => {
    // Fetch resources
    const fetchResources = async () => {
      try {
        const [attRes, wbRes, vidRes, hwRes, qzRes, mrRes] = await Promise.all([
          getAttendance(id).catch(() => ({ data: null })),
          getClassWorkBooks(id).catch(() => ({ data: null })),
          listClassVideos(id).catch(() => ({ data: null })),
          listClassHomeWork(id).catch(() => ({ data: null })),
          listClassQuiz(id).catch(() => ({ data: null })),
          listClassMultipleRecording(id).catch(() => ({ data: null }))
        ]);
        if (attRes?.data?.length) setAttendance(attRes.data);
        if (wbRes?.data?.length) setWorkbooks(wbRes.data);
        if (vidRes?.data?.length) setVideos(vidRes.data);
        if (hwRes?.data?.length) setHomework(hwRes.data);
        if (qzRes?.data?.length) setQuizzes(qzRes.data);
        if (mrRes?.data?.length) setMultipleRecordings(mrRes.data);
      } catch (err) {
        console.warn('Failed to fetch resources:', err);
      }
    };
    fetchResources();
  }, [id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (studentEmail && studentEmail.length > 2) {
        setSearching(true);
        try {
          const res = await searchStudentsApi({ search: studentEmail });
          setSearchResults(res.data || res.users || []);
        } catch (error) {
          console.error("Failed to search students", error);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [studentEmail]);

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student?')) return;
    try {
      const updatedStudentIds = students
        .map(s => s.id)
        .filter(id => id !== studentId);
      await updateClass(id, { students: updatedStudentIds });
      fetchClassDetails();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove student.');
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!studentEmail) {
      alert('Please provide student email');
      return;
    }
    try {
      setSubmitting(true);

      let userIdToAssign = selectedUserId;

      if (!userIdToAssign) {
        const res = await searchStudentsApi({ search: studentEmail });
        const users = res.data || res.users || [];
        const match = users.find(u => u.email === studentEmail);
        if (match) {
          userIdToAssign = match._id;
        } else {
          alert('Student not found with this email.');
          setSubmitting(false);
          return;
        }
      }

      const currentStudentIds = students.map(s => s.id);
      if (currentStudentIds.includes(userIdToAssign)) {
        alert("Student is already enrolled in this class.");
        setSubmitting(false);
        return;
      }

      const updatedStudentIds = [...currentStudentIds, userIdToAssign];
      await updateClass(id, { students: updatedStudentIds });

      setShowAssignForm(false);
      setStudentEmail('');
      setSelectedUserId(null);
      fetchClassDetails();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign student.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRecording = (e) => {
    e.preventDefault();
    if (!newRecordingTitle.trim()) return;
    const newRec = {
      id: 'rec-' + Date.now(),
      title: newRecordingTitle,
      date: new Date().toISOString().split('T')[0],
      url: newRecordingUrl || '#'
    };
    setRecordings(prev => [...prev, newRec]);
    setNewRecordingTitle('');
    setNewRecordingUrl('');
  };

  const handleAttendanceChange = async (studentId, newStatus) => {
    try {
      await markAttendance({ classId: id, studentId, status: newStatus });
      setAttendance(prev =>
        prev.map(a => (a.id === studentId ? { ...a, status: newStatus } : a))
      );
    } catch (err) {
      console.warn('Attendance API failed, using fallback:', err);
      setAttendance(prev =>
        prev.map(a => (a.id === studentId ? { ...a, status: newStatus } : a))
      );
    }
  };

  const handleExportAttendance = async () => {
    try {
      const response = await exportAttendance(id);
      if (response && response.url) {
        const link = document.createElement('a');
        link.href = response.url;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Attendance exported successfully!');
      }
    } catch (err) {
      alert('Failed to export attendance');
    }
  };

  const handleDeleteResource = async (type, resourceId) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      if (type === 'workbooks') { await deleteClassWorkBook(resourceId); setWorkbooks(prev => prev.filter(i => i.id !== resourceId && i._id !== resourceId)); }
      else if (type === 'videos') { await deleteClassVideo(resourceId); setVideos(prev => prev.filter(i => i.id !== resourceId && i._id !== resourceId)); }
      else if (type === 'homework') { await deleteClassHomeWork(resourceId); setHomework(prev => prev.filter(i => i.id !== resourceId && i._id !== resourceId)); }
      else if (type === 'quizzes') { await deleteClassQuiz(resourceId); setQuizzes(prev => prev.filter(i => i.id !== resourceId && i._id !== resourceId)); }
      else if (type === 'recordings') { await deleteClassMultipleRecording(resourceId); setMultipleRecordings(prev => prev.filter(i => i.id !== resourceId && i._id !== resourceId)); }
    } catch (err) {
      alert('Failed to delete resource');
    }
  };

  const handleOpenAddModal = (type) => {
    setAddType(type);
    setAddFormData({ title: '', url: '', description: '', dueDate: '', totalMarks: '' });
    setShowAddModal(true);
  };

  const handleSubmitResource = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { classId: id, ...addFormData };
      let res;
      if (addType === 'workbooks') { res = await createClassWorkBook(payload); setWorkbooks(prev => [...prev, res?.data || { id: Date.now(), ...payload }]); }
      else if (addType === 'videos') { res = await addClassVideo(payload); setVideos(prev => [...prev, res?.data || { id: Date.now(), ...payload }]); }
      else if (addType === 'homework') { res = await addClassHomeWork(payload); setHomework(prev => [...prev, res?.data || { id: Date.now(), ...payload }]); }
      else if (addType === 'quizzes') { res = await createClassQuiz(payload); setQuizzes(prev => [...prev, res?.data || { id: Date.now(), ...payload }]); }
      else if (addType === 'recordings') { res = await addClassMultipleRecording(payload); setMultipleRecordings(prev => [...prev, res?.data || { id: Date.now(), ...payload }]); }
      setShowAddModal(false);
    } catch (err) {
      alert('Failed to add resource');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBook = async () => {
    if (!isAuthenticated) {
      setAuthAction('join this live class');
      setShowAuthModal(true);
      return;
    }

    if (classData?.cost > 0 || classData?.is_paid === 'paid') {
      setShowPaypal(true);
      return;
    }

    // Free class booking
    try {
      setIsBooking(true);
      await joinClass(id, { payment_method: 'free' });
      alert('Class joined successfully!');
      if (classData?.joinLink) {
        if (user && user._id) {
          try {
            await markAttendance({ classId: id, studentId: user._id, status: 'Present' });
          } catch (e) {
            console.warn('Failed to mark attendance:', e);
          }
        }
        window.open(getValidUrl(classData.joinLink), '_blank');
      }
      fetchClassDetails();
    } catch (err) {
      console.error('Failed to book class:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to book class.');
    } finally {
      setIsBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center loading-wrapper">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error || !classData) {
    return (
      <Container className="py-5 text-center">
        <h3 className="text-danger">{error || 'Class details not found'}</h3>
        <Link to="/classes" className="btn btn-primary mt-3">Back to Classes</Link>
      </Container>
    );
  }

  const currentUserId = user?._id || user?.id;
  const currentUserEmail = user?.email;

  const instructor = classData.instructor || classData.teacher;
  const teacherName = (instructor && typeof instructor === 'object' && instructor.full_name)
    ? instructor.full_name
    : 'Yoospeak Instructor';
  const teacherId = (instructor && typeof instructor === 'object' && instructor._id)
    ? instructor._id
    : (typeof instructor === 'string' ? instructor : '');
  const teacherAvatar = (instructor && typeof instructor === 'object' && instructor.profile_picture)
    ? instructor.profile_picture
    : 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop';

  const isUserTeacher = Boolean(
    currentUserId && (
      teacherId === currentUserId ||
      classData.teacher === currentUserId ||
      (typeof classData.teacher === 'object' && classData.teacher?._id === currentUserId) ||
      (typeof classData.instructor === 'object' && classData.instructor?._id === currentUserId) ||
      user?.user_type === 'teacher' ||
      user?.role === 'teacher'
    )
  );

  const isUserEnrolled = Boolean(
    classData.is_enrolled_bool ||
    classData.is_enrolled ||
    classData.is_booked ||
    isUserTeacher ||
    (Array.isArray(students) && currentUserId && students.some(s =>
      (s.id && s.id === currentUserId) ||
      (s._id && s._id === currentUserId) ||
      (s.email && currentUserEmail && s.email.toLowerCase() === currentUserEmail.toLowerCase())
    ))
  );

  const isTeacher = isUserTeacher;
  const enrolledCount = students.length;

  const isStarted = Boolean(
    classData.started === true ||
    classData.status === 'started' ||
    classData.is_started === true ||
    classData.status === 'live' ||
    classData.is_live === true
  );
  const isCompleted = Boolean(classData.completed === true || classData.status === 'completed' || classData.is_completed === true);
  const isYetToStart = Boolean(classData.yet_to_start === true || classData.status === 'yet_to_start' || (!isStarted && !isCompleted));

  const localStart = classData.start_date ? new Date(classData.start_date) : null;
  const localEnd = classData.end_date ? new Date(classData.end_date) : null;

  const startDateStr = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleDateString('en-GB')
    : 'TBD';

  const startTimeStr = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (classData.start_time || 'TBD');

  const endTimeStr = localEnd && !isNaN(localEnd.getTime())
    ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (classData.end_time || 'TBD');

  const isObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);
  const languageName = (classData.language && typeof classData.language === 'object')
    ? classData.language.name
    : (classData.language && !isObjectId(classData.language) ? classData.language : 'English');

  return (
    <div className="community-detail-page class-detail-page py-4">
      <SEO title={classData.title} description={classData.description} image={classData.cover_image || classData.image} />
      <AuthModal show={showAuthModal} onHide={() => setShowAuthModal(false)} title="Continue Learning" actionText={authAction} />
      <Container fluid="xl">
        {/* Top Header Banner Box */}
        <section className="profile-banner-section bg-white border rounded-4 overflow-hidden mb-4">
          {/* Banner Cover Image */}
          <div className="cd-banner">
            <img
              src={classData.cover_image || classData.image || CLASS_FALLBACK}
              alt={classData.title}
              className="cd-banner__img"
              onError={(e) => { e.target.src = CLASS_FALLBACK; }}
            />
            {/* Share action button overlay */}
            <div className="cd-banner__actions">
              <button className="cd-btn-share" onClick={() => alert('Link copied to clipboard!')}>
                <i className="bi bi-share"></i> Share
              </button>
            </div>
          </div>

          {/* Info Overlapping Row */}
          <div className="cd-info-card bg-white p-4">
            <div className="cd-info-top">
              {/* Logo / overlapping instructor avatar */}
              <div className="cd-logo-wrap">
                <img
                  src={teacherAvatar}
                  alt={teacherName}
                  className="cd-logo border border-3 border-white rounded-circle object-fit-cover shadow"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop'; }}
                />
              </div>

              {/* Title + Subtitle + Badge tags */}
              <div className="cd-info-body">
                <div className="cd-name text-dark mb-1">{classData.title}</div>
                <p className="cd-subtitle d-flex align-items-center gap-1">
                  Instructor: {teacherName}
                  {instructor?.is_blue_verified && <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '0.85rem' }} title="Verified Teacher" />}
                </p>
                <div className="cd-tags">
                  <span className="cd-tag cd-tag--lang">
                    <i className="bi bi-translate"></i> {languageName}
                  </span>
                  <span className="cd-tag cd-tag--students">
                    <i className="bi bi-people-fill"></i> {enrolledCount} / {classData.max_students || 1} Seats Enrolled
                  </span>
                  <span className="cd-tag cd-tag--cat">
                    <i className="bi bi-cash"></i> {classData.cost > 0 ? `$${classData.cost} Class` : 'Free Class'}
                  </span>
                </div>
              </div>

              {/* Action Button: Book Now / Join */}
              <div className="cd-info-actions">
                {isStarted ? (
                  <Button
                    className="cd-btn-joined border-0"
                    onClick={async () => {
                      if (classData.joinLink) {
                        if (user && user._id) {
                          try {
                            await markAttendance({ classId: id, studentId: user._id, status: 'Present' });
                          } catch (e) {
                            console.warn("Failed to mark attendance automatically:", e);
                          }
                        }
                        window.open(getValidUrl(classData.joinLink), '_blank');
                      } else {
                        alert("Class join link is not available yet.");
                      }
                    }}
                  >
                    <i className="bi bi-camera-video-fill me-1"></i> Join Now
                  </Button>
                ) : (isUserEnrolled || isUserTeacher) ? (
                  <Button className="cd-btn-joined border-0" disabled style={{ opacity: 0.9 }}>
                    <i className="bi bi-check-circle-fill me-1"></i> Booked
                  </Button>
                ) : showPaypal ? (
                  <div className="mt-3 w-100">
                    <PayPalScriptProvider options={{ "client-id": "AcEBlYxSTDKF51XKtEB_L4-X18Q4kpoyUj0yY6DggYqVCssqh9nJbcUESyx6Fp_HLJnPy3msNxmjttaG", currency: "USD" }}>
                      <PayPalButtons
                        style={{ layout: "horizontal", height: 40 }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: { value: (classData.cost || classData.class_fees || 0).toString() }
                            }]
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then(async (details) => {
                            try {
                              setIsBooking(true);
                              const txRes = await storeTransaction({
                                amount: classData.cost || classData.class_fees || 0,
                                status: 'succeeded',
                                currency: 'usd',
                                charge_id: details.id,
                                payment_method: 'paypal',
                                transaction_type: 'class_enrollment'
                              });

                              // Note: API might return status 200 inside JSON
                              if (txRes && (txRes.status === 200 || txRes.status === 'succeeded' || txRes.data)) {
                                await joinClass(id, { payment_method: 'paypal', transaction_id: details.id });
                                setShowPaypal(false);
                                alert('Class booked successfully!');
                                if (classData?.joinLink) {
                                  if (user && user._id) {
                                    try {
                                      await markAttendance({ classId: id, studentId: user._id, status: 'Present' });
                                    } catch (e) {
                                      console.warn('Failed to mark attendance:', e);
                                    }
                                  }
                                  window.open(getValidUrl(classData.joinLink), '_blank');
                                }
                                fetchClassDetails();
                              } else {
                                alert('Failed to store transaction. Please contact support.');
                                setShowPaypal(false);
                              }
                            } catch (err) {
                              console.error(err);
                              alert(err?.response?.data?.message || 'Payment successful but failed to join class on server.');
                            } finally {
                              setIsBooking(false);
                            }
                          });
                        }}
                        onCancel={() => setShowPaypal(false)}
                        onError={async (err) => {
                          console.error("PayPal Error:", err);
                          try {
                            await storeTransaction({
                              amount: classData.cost || classData.class_fees || 0,
                              status: 'failed',
                              currency: 'usd',
                              charge_id: '',
                              payment_method: 'paypal',
                              transaction_type: 'class_enrollment'
                            });
                          } catch (e) {
                            console.error("Failed to store failed transaction:", e);
                          }
                          alert("Payment failed or was cancelled.");
                          setShowPaypal(false);
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>
                ) : (
                  <Button
                    className="cd-btn-joined border-0"
                    onClick={handleBook}
                    disabled={isBooking}
                  >
                    {isBooking ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-lightning-charge-fill me-1"></i>}
                    Book Now
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Details Panel Layout */}
        <Row className="g-4">
          {/* Left Main Details Column */}
          <Col lg={8}>
            {/* About Class Panel */}
            <div className="cd-panel mb-4">
              <h5 className="cd-panel__title"><i className="bi bi-info-circle text-primary me-2"></i>About Class</h5>
              <p className="cd-panel__text class-description-text">{classData.description}</p>

              {classData.access_type && (
                <div className="mt-3 d-flex align-items-center gap-2 text-gray-700 fs-6">
                  <strong>Access:</strong>
                  <span className="text-muted">{classData.access_type}</span>
                </div>
              )}
            </div>

            {/* Key points Panel */}
            {classData.keypoints && classData.keypoints.length > 0 && (
              <div className="cd-panel mb-4">
                <h5 className="cd-panel__title"><i className="bi bi-check-circle-fill text-primary me-2"></i>Key Learnings</h5>
                <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                  {classData.keypoints.map((point, index) => (
                    <li key={index} className="d-flex align-items-start gap-2 fs-6 text-gray-700">
                      <i className="bi bi-check-circle-fill text-primary mt-1"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Four Dashboard Panel Cards */}
            <Row className="g-3">
              <Col sm={6}>
                <div className="cd-panel h-100 d-flex flex-column align-items-center text-center p-4">
                  <div className="icon-badge rounded-circle mb-3">
                    <i className="bi bi-people-fill text-primary"></i>
                  </div>
                  <h6 className="fw-bold mb-2">{enrolledCount} Students Enrolled</h6>
                  <Button className="btn-grid-action mt-auto rounded-pill py-2 px-4 fw-semibold text-white border-0" onClick={() => setShowStudentsModal(true)}>
                    Assign Students
                  </Button>
                </div>
              </Col>

              <Col sm={6}>
                <div className="cd-panel h-100 d-flex flex-column align-items-center text-center p-4 position-relative">
                  {isTeacher && (
                    <button
                      className="btn btn-primary btn-sm rounded-circle position-absolute btn-add-recording"
                      title="Add Recording"
                      onClick={() => setShowRecordingsModal(true)}
                    >
                      <i className="bi bi-plus-lg text-white"></i>
                    </button>
                  )}
                  <div className="icon-badge rounded-circle mb-3">
                    <i className="bi bi-mic-fill text-primary"></i>
                  </div>
                  <h6 className="fw-bold mb-2">{recordings.length} Class Recordings</h6>
                  <Button className="btn-grid-action mt-auto rounded-pill py-2 px-4 fw-semibold text-white border-0" onClick={() => setShowRecordingsModal(true)}>
                    View Recordings
                  </Button>
                </div>
              </Col>

              <Col sm={6}>
                <div className="cd-panel h-100 d-flex flex-column align-items-center text-center p-4">
                  <div className="icon-badge rounded-circle mb-3">
                    <i className="bi bi-calendar-check text-primary"></i>
                  </div>
                  <h6 className="fw-bold mb-2">Recent Attendance</h6>
                  <Button className="btn-grid-action mt-auto rounded-pill py-2 px-4 fw-semibold text-white border-0" onClick={() => setShowAttendanceModal(true)}>
                    View Attendance
                  </Button>
                </div>
              </Col>

              <Col sm={6}>
                <div className="cd-panel h-100 d-flex flex-column align-items-center text-center p-4">
                  <div className="icon-badge rounded-circle mb-3">
                    <i className="bi bi-folder-fill text-primary"></i>
                  </div>
                  <h6 className="fw-bold mb-2 text-muted text-sm px-2">Homework, Videos, Quizzes & books...</h6>
                  <Button className="btn-grid-action mt-auto rounded-pill py-2 px-4 fw-semibold text-white border-0" onClick={() => setShowDocumentsModal(true)}>
                    View Documents
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>

          {/* Right Sidebar Widget Column */}
          <Col lg={4}>
            {/* Class Schedule Panel */}
            <div className="cd-panel mb-4">
              <h5 className="cd-panel__title border-bottom pb-2 d-flex align-items-center gap-2">
                <i className="bi bi-calendar-event text-primary"></i> Class Schedule
              </h5>
              <div className="d-flex flex-column gap-3 fs-6 mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">Start Date:</span>
                  <strong className="text-gray-900">{startDateStr}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">Start Time:</span>
                  <strong className="text-gray-900">{startTimeStr}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">End Time:</span>
                  <strong className="text-gray-900">{endTimeStr}</strong>
                </div>
              </div>
            </div>

            {/* Sizing panel */}
            <div className="cd-panel mb-4">
              <h5 className="cd-panel__title border-bottom pb-2">Class Sizing</h5>
              <div className="d-flex flex-column gap-3 fs-6 mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">Class Size:</span>
                  <strong className="text-gray-900">{classData.max_students || 1}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">Students Enrolled:</span>
                  <strong className="text-gray-900">{enrolledCount}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-gray-500">Seats Left:</span>
                  <strong className="text-primary">{Math.max(0, (classData.max_students || 1) - enrolledCount)}</strong>
                </div>
              </div>
            </div>

            {/* Schedule Repeats Panel */}
            {(classData.isRecurrence || classData.is_recurrence || classData.isRecurring) && (
              <div className="cd-panel mb-4">
                <h5 className="cd-panel__title border-bottom pb-2 d-flex align-items-center gap-2">
                  <i className="bi bi-arrow-repeat text-primary"></i> Repeats
                </h5>
                <div className="bg-light p-3 rounded-3 text-sm mt-3">
                  <div className="d-flex align-items-center gap-2 mb-2 text-gray-700 fw-bold">
                    <i className="bi bi-clock"></i>
                    <span>
                      Repeats every {classData.repeatEvery || classData.repeat_every || 1} {classData.repeatType || classData.repeat_type || 'week'}(s)
                    </span>
                  </div>
                  {((classData.repeatType || classData.repeat_type || 'week') === 'week') && (
                    <div className="d-flex flex-wrap gap-2 my-3">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                        const classDays = classData.selectedDays || classData.selected_days || [];
                        const parsedClassDays = Array.isArray(classDays)
                          ? classDays
                          : (typeof classDays === 'string' ? JSON.parse(classDays) : []);
                        const isScheduled = parsedClassDays.includes(day);
                        return (
                          <span
                            key={day}
                            className={`repeats-day-badge ${isScheduled ? 'active' : 'inactive'}`}
                          >
                            {day}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {classData.endType === 'afterOccurrences' && (
                    <div className="text-xs text-gray-500">
                      <i className="bi bi-flag-fill me-1"></i> Ends after {classData.occurrences} occurrences
                    </div>
                  )}
                  {classData.endType === 'onDate' && (
                    <div className="text-xs text-gray-500">
                      <i className="bi bi-flag-fill me-1"></i> Ends on {classData.recurrenceEndDate ? new Date(classData.recurrenceEndDate).toLocaleDateString('en-GB') : 'TBD'}
                    </div>
                  )}
                  {(classData.endType === 'never' || !classData.endType) && (
                    <div className="text-xs text-gray-500">
                      <i className="bi bi-flag-fill me-1"></i> Never ends
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Class Status Panel - Single button based on status keys */}
            <div className="cd-panel text-center">
              {isCompleted ? (
                <Button variant="secondary" size="lg" className="w-100 rounded-pill py-3 fw-bold text-white shadow border-0" style={{ backgroundColor: '#6b7280' }} disabled>
                  <i className="bi bi-check-circle-fill me-2"></i> Class Completed
                </Button>
              ) : isStarted ? (
                <Button
                  variant="success"
                  size="lg"
                  className="w-100 rounded-pill py-3 fw-bold text-white shadow border-0"
                  style={{ backgroundColor: '#10b981' }}
                  onClick={async () => {
                    if (classData.joinLink) {
                      if (user && user._id) {
                        try {
                          await markAttendance({ classId: id, studentId: user._id, status: 'Present' });
                        } catch (e) {
                          console.warn('Failed to mark attendance:', e);
                        }
                      }
                      window.open(getValidUrl(classData.joinLink), '_blank');
                    } else {
                      alert('Class join link is not available yet.');
                    }
                  }}
                >
                  <i className="bi bi-camera-video-fill me-2"></i> Join Now
                </Button>
              ) : (
                <Button variant="warning" size="lg" className="w-100 rounded-pill py-3 fw-bold shadow border-0" style={{ backgroundColor: '#fffbe6', color: '#d97706', border: '1.5px solid #fcd34d' }} disabled>
                  <i className="bi bi-clock-history me-2"></i> Yet to start
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* MODAL 1: Students */}
      <Modal show={showStudentsModal} onHide={() => { setShowStudentsModal(false); setShowAssignForm(false); }} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Students Enrolled</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {showAssignForm ? (
            <div className="bg-light p-3 rounded-4 mb-3 border">
              <h6 className="fw-bold mb-3">Assign New Student</h6>
              <Form onSubmit={handleAssignSubmit}>
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Student Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter student email to search..."
                    value={studentEmail}
                    onChange={e => { setStudentEmail(e.target.value); setSelectedUserId(null); }}
                    autoComplete="off"
                    required
                  />
                  {studentEmail && studentEmail.length > 2 && searchResults.length > 0 && (
                    <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                      {searchResults.map((user) => (
                        <div
                          key={user._id}
                          className="p-2 border-bottom pointer text-dark d-flex align-items-center gap-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setStudentEmail(user.email);
                            setSelectedUserId(user._id);
                            setSearchResults([]);
                          }}
                        >
                          {user.profile_picture ? (
                            <img src={user.profile_picture} alt="Profile" className="rounded-circle object-fit-cover" style={{ width: 30, height: 30 }} />
                          ) : (
                            <i className="bi bi-person-circle text-muted fs-4"></i>
                          )}
                          <div>
                            <div className="fw-bold small">{user.full_name || 'Student'}</div>
                            <div className="text-muted small">{user.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {studentEmail && studentEmail.length > 2 && searchResults.length === 0 && !searching && (
                    <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-center text-muted small" style={{ zIndex: 10 }}>
                      No students found.
                    </div>
                  )}
                  {searching && (
                    <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-center text-muted small" style={{ zIndex: 10 }}>
                      Searching...
                    </div>
                  )}
                </Form.Group>
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button variant="light" className="rounded-pill" onClick={() => setShowAssignForm(false)}>Cancel</Button>
                  <Button variant="primary" type="submit" disabled={submitting} className="rounded-pill px-4 border-0 btn-custom" style={{ backgroundColor: '#ff6b35' }}>
                    {submitting ? 'Assigning...' : 'Assign Student'}
                  </Button>
                </div>
              </Form>
            </div>
          ) : (
            <>
              {isTeacher && (
                <div className="d-flex justify-content-end mb-3">
                  <Button variant="primary" className="rounded-pill px-4 border-0 btn-custom" style={{ backgroundColor: '#ff6b35', color: '#fff' }} onClick={() => setShowAssignForm(true)}>
                    <i className="bi bi-person-plus-fill me-1"></i> Assign Student
                  </Button>
                </div>
              )}

              {students.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people text-muted" style={{ fontSize: '3rem' }}></i>
                  <h5 className="mt-3">No Students Enrolled</h5>
                  <p className="text-muted">No students have been assigned to this class yet.</p>
                </div>
              ) : (
                <Row className="g-3">
                  {students.map((item, index) => (
                    <Col md={6} key={`${item.id}-${index}`}>
                      <div className="card h-100 border-0 shadow-sm rounded-4 position-relative p-3">
                        {isTeacher && (
                          <button
                            className="btn btn-sm btn-outline-danger position-absolute top-50 translate-middle-y end-0 me-3 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleRemoveStudent(item.id)}
                            title="Remove Student"
                            style={{ zIndex: 2, width: '32px', height: '32px' }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-light rounded-circle d-flex align-items-center justify-content-center overflow-hidden" style={{ width: 50, height: 50 }}>
                            {item.profilePicture ? (
                              <img src={item.profilePicture} alt="Profile" className="w-100 h-100 object-fit-cover" />
                            ) : (
                              <i className="bi bi-person-fill text-secondary fs-4"></i>
                            )}
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">
                              {item.fullName || 'Student'}
                            </h6>
                            <small className="text-muted">
                              {item.email || ''}
                            </small>
                            <div className="mt-1">
                              <Badge bg="success" className="rounded-pill" style={{ backgroundColor: '#2ec4b6' }}>Enrolled</Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => { setShowStudentsModal(false); setShowAssignForm(false); }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 2: Recordings */}
      <Modal show={showRecordingsModal} onHide={() => setShowRecordingsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Class Recordings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isTeacher && (
            <Form onSubmit={handleAddRecording} className="mb-4 p-3 bg-light rounded-3">
              <h6 className="fw-bold mb-3"><i className="bi bi-plus-circle text-primary me-2"></i>Add New Recording</h6>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Recording Title"
                    value={newRecordingTitle}
                    onChange={(e) => setNewRecordingTitle(e.target.value)}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    type="text"
                    placeholder="Video/Audio Link"
                    value={newRecordingUrl}
                    onChange={(e) => setNewRecordingUrl(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Button type="submit" variant="primary" className="w-100">Add</Button>
                </Col>
              </Row>
            </Form>
          )}

          {recordings.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-mic fs-1 mb-2"></i>
              <p>No class recordings uploaded yet.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {recordings.map((rec) => (
                <Card key={rec.id} className="border border-light-subtle shadow-sm rounded-3">
                  <Card.Body className="d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 text-primary rounded p-2 fs-4">
                        <i className="bi bi-play-circle-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">{rec.title}</h6>
                        <small className="text-muted">Recorded on {rec.date}</small>
                      </div>
                    </div>
                    <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={(e) => { e.preventDefault(); window.open(rec.url, '_blank'); }}>
                      <i className="bi bi-play-fill me-1"></i>Play
                    </Button>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecordingsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 3: Attendance */}
      <Modal show={showAttendanceModal} onHide={() => setShowAttendanceModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Log</Modal.Title>
          {isTeacher && (
            <Button variant="outline-primary" size="sm" className="ms-auto me-2" onClick={handleExportAttendance}>
              <i className="bi bi-download me-1"></i> Export
            </Button>
          )}
        </Modal.Header>
        <Modal.Body>
          <Table responsive hover align="middle" className="text-sm">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Status</th>
                <th className="text-end">Mark Status</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map(a => (
                <tr key={a.id}>
                  <td className="fw-bold">{a.name}</td>
                  <td>
                    <Badge bg={a.status === 'Present' ? 'success' : 'danger'}>
                      {a.status}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <Button
                        variant={a.status === 'Present' ? 'success' : 'outline-success'}
                        size="sm"
                        className="rounded-pill px-3 py-1 btn-status-small"
                        onClick={() => handleAttendanceChange(a.id, 'Present')}
                      >
                        Present
                      </Button>
                      <Button
                        variant={a.status === 'Absent' ? 'danger' : 'outline-danger'}
                        size="sm"
                        className="rounded-pill px-3 py-1 btn-status-small"
                        onClick={() => handleAttendanceChange(a.id, 'Absent')}
                      >
                        Absent
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttendanceModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 4: Class Documents */}
      <Modal show={showDocumentsModal} onHide={() => setShowDocumentsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Class Resources</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            <Tab eventKey="workbooks" title="Workbooks">
              {workbooks.map(wb => (
                <Card key={wb.id || wb._id} className="mb-2 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-bold"><i className="bi bi-book me-2 text-primary"></i>{wb.title}</h6>
                    </div>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => window.open(wb.url, '_blank')}><i className="bi bi-eye"></i> View</Button>
                      {isTeacher && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResource('workbooks', wb.id || wb._id)}><i className="bi bi-trash"></i></Button>}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {isTeacher && <Button variant="primary" size="sm" className="mt-2" onClick={() => handleOpenAddModal('workbooks')}><i className="bi bi-plus-circle me-1"></i> Add Workbook</Button>}
            </Tab>

            <Tab eventKey="videos" title="Videos">
              {videos.map(vid => (
                <Card key={vid.id || vid._id} className="mb-2 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-bold"><i className="bi bi-camera-video me-2 text-primary"></i>{vid.title}</h6>
                    </div>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => window.open(vid.url, '_blank')}><i className="bi bi-play-fill"></i> Play</Button>
                      {isTeacher && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResource('videos', vid.id || vid._id)}><i className="bi bi-trash"></i></Button>}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {isTeacher && <Button variant="primary" size="sm" className="mt-2" onClick={() => handleOpenAddModal('videos')}><i className="bi bi-plus-circle me-1"></i> Add Video</Button>}
            </Tab>

            <Tab eventKey="homework" title="Homework">
              {homework.map(hw => (
                <Card key={hw.id || hw._id} className="mb-2 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-bold"><i className="bi bi-pencil-square me-2 text-primary"></i>{hw.title}</h6>
                      <small className="text-muted">Due: {hw.dueDate}</small>
                    </div>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => alert(`Viewing Homework: ${hw.description || 'Details not provided.'}`)}><i className="bi bi-eye"></i> View</Button>
                      {isTeacher && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResource('homework', hw.id || hw._id)}><i className="bi bi-trash"></i></Button>}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {isTeacher && <Button variant="primary" size="sm" className="mt-2" onClick={() => handleOpenAddModal('homework')}><i className="bi bi-plus-circle me-1"></i> Add Homework</Button>}
            </Tab>

            <Tab eventKey="quizzes" title="Quizzes">
              {quizzes.map(qz => (
                <Card key={qz.id || qz._id} className="mb-2 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-bold"><i className="bi bi-question-circle me-2 text-primary"></i>{qz.title}</h6>
                      <small className="text-muted">Marks: {qz.totalMarks}</small>
                    </div>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => window.open(`/quiz/${qz.id || qz._id}`, '_blank')}><i className="bi bi-pencil"></i> Start</Button>
                      {isTeacher && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResource('quizzes', qz.id || qz._id)}><i className="bi bi-trash"></i></Button>}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {isTeacher && <Button variant="primary" size="sm" className="mt-2" onClick={() => handleOpenAddModal('quizzes')}><i className="bi bi-plus-circle me-1"></i> Create Quiz</Button>}
            </Tab>

            {/* <Tab eventKey="recordings" title="Multiple Recordings">
              {multipleRecordings.map(mr => (
                <Card key={mr.id || mr._id} className="mb-2 shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 fw-bold"><i className="bi bi-mic me-2 text-primary"></i>{mr.title}</h6>
                    </div>
                    <div>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => window.open(mr.url, '_blank')}><i className="bi bi-play-fill"></i> Play</Button>
                      {isTeacher && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteResource('recordings', mr.id || mr._id)}><i className="bi bi-trash"></i></Button>}
                    </div>
                  </Card.Body>
                </Card>
              ))}
              {isTeacher && <Button variant="primary" size="sm" className="mt-2" onClick={() => handleOpenAddModal('recordings')}><i className="bi bi-plus-circle me-1"></i> Add Recording</Button>}
            </Tab> */}
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDocumentsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 5: Unified Add Resource */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-capitalize">Add {addType}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitResource}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" required value={addFormData.title} onChange={e => setAddFormData({ ...addFormData, title: e.target.value })} />
            </Form.Group>
            {['workbooks', 'videos', 'recordings'].includes(addType) && (
              <Form.Group className="mb-3">
                <Form.Label>URL Link</Form.Label>
                <Form.Control type="url" required value={addFormData.url} onChange={e => setAddFormData({ ...addFormData, url: e.target.value })} />
              </Form.Group>
            )}
            {addType === 'homework' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" rows={3} required value={addFormData.description} onChange={e => setAddFormData({ ...addFormData, description: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control type="date" required value={addFormData.dueDate} onChange={e => setAddFormData({ ...addFormData, dueDate: e.target.value })} />
                </Form.Group>
              </>
            )}
            {addType === 'quizzes' && (
              <Form.Group className="mb-3">
                <Form.Label>Total Marks</Form.Label>
                <Form.Control type="number" required value={addFormData.totalMarks} onChange={e => setAddFormData({ ...addFormData, totalMarks: e.target.value })} />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Add'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};
